/**
 * Phrenos Brand Assets
 *
 * Usage:
 *   import { PhrenosIcon, PhrenosWordmark, PhrenesSplash, AppHeader } from './phrenos-brand'
 *
 * Logo: Phi (Φ) — reads as the Greek letter and a target simultaneously.
 *   Ring  = #478475 confirmation green  (the phi circle / focus zone)
 *   Stem  = white                       (the phi vertical / crosshair)
 *   Dot   = white                       (the focal point / bull)
 *   Tile  = #1F4C40 primary dark green
 *
 * Tagline: "Study sharper."
 * Domain:  phrenos.app
 *
 * ETS disclaimer — required wherever GRE is mentioned:
 *   "GRE® is a registered trademark of ETS.
 *    Phrenos is not affiliated with or endorsed by ETS."
 */

import React from 'react'

// ─────────────────────────────────────────────
// Color tokens — mirrors AppColors in Dart
// ─────────────────────────────────────────────
export const colors = {
  primary:        '#1F4C40',
  confirmation:   '#478475',
  accent:         '#FFFFFF',
  background:     '#E6F1EC',
  card:           '#F7F7F7',
  textPrimary:    '#222222',
  textSecondary:  '#707070',
  border:         '#E0E0E0',
  button:         '#23483F',
  buttonText:     '#FFFFFF',
  iconBgReversed: '#2d6454',
  mintText:       '#9FE1CB',
  successLight:   '#D4EDE6',
  danger:         '#C0392B',
} as const

// ─────────────────────────────────────────────
// PhrenosIcon — the phi/target mark
//
// variant:
//   'dark'     — #1F4C40 tile  (default, use on light bgs)
//   'reversed' — #2d6454 tile  (use on dark / splash bgs)
//   'light'    — no tile       (use when bg is already colored)
// ─────────────────────────────────────────────
interface IconProps {
  size?:    number
  variant?: 'dark' | 'reversed' | 'light'
}

export function PhrenosIcon({ size = 40, variant = 'dark' }: IconProps) {
  const tileFill  = variant === 'dark'     ? colors.primary
                  : variant === 'reversed' ? colors.iconBgReversed
                  : 'none'
  const ringColor = variant === 'reversed' ? colors.mintText : colors.confirmation

  // Stroke weight scales so the mark stays legible at tiny sizes
  const sw = size >= 40 ? 4.5 : size >= 28 ? 5 : 6

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Phrenos"
    >
      <rect width="120" height="120" rx="26" fill={tileFill} />

      {/* Phi ring — confirmation green */}
      <circle cx="60" cy="60" r="27" fill="none" stroke={ringColor} strokeWidth={sw} />

      {/* Phi vertical stem — white, extends beyond ring top + bottom */}
      <line x1="60" y1="16" x2="60" y2="104" stroke="#FFFFFF" strokeWidth={sw} strokeLinecap="round" />

      {/* Center dot — focal point */}
      <circle cx="60" cy="60" r={sw * 1.1} fill="#FFFFFF" />
    </svg>
  )
}

// ─────────────────────────────────────────────
// PhrenosWordmark — icon + "phrenos" logotype
// ─────────────────────────────────────────────
interface WordmarkProps {
  iconSize?:    number
  variant?:     'default' | 'reversed'
  showTagline?: boolean
}

export function PhrenosWordmark({
  iconSize    = 32,
  variant     = 'default',
  showTagline = false,
}: WordmarkProps) {
  const isReversed = variant === 'reversed'
  const nameColor  = isReversed ? '#FFFFFF'       : colors.primary
  const tagColor   = isReversed ? colors.mintText : colors.textSecondary

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: Math.round(iconSize * 0.3) }}>
      <PhrenosIcon size={iconSize} variant={isReversed ? 'reversed' : 'dark'} />
      <div>
        <span style={{
          fontSize:      Math.round(iconSize * 0.65),
          fontWeight:    800,
          letterSpacing: '-0.5px',
          color:         nameColor,
          lineHeight:    1,
          display:       'block',
          fontFamily:    '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
          phrenos
        </span>
        {showTagline && (
          <span style={{
            fontSize:   Math.round(iconSize * 0.32),
            color:      tagColor,
            display:    'block',
            marginTop:  3,
            lineHeight: 1,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}>
            Study sharper.
          </span>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// AppHeader — slim 48px in-app header
// ─────────────────────────────────────────────
interface AppHeaderProps {
  onProfilePress?: () => void
  planBadge?: 'PRO' | 'TRIAL'
}

export function AppHeader({ onProfilePress, planBadge }: AppHeaderProps) {
  return (
    <div style={{
      height:         48,
      background:     colors.background,
      borderBottom:   `1px solid ${colors.border}`,
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'space-between',
      padding:        '0 18px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <PhrenosWordmark iconSize={26} variant="default" />
        {planBadge && (
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
            backgroundColor: planBadge === 'PRO' ? colors.primary : '#D97706',
            color: '#fff',
            padding: '2px 7px', borderRadius: 999,
          }}>
            {planBadge}
          </span>
        )}
      </div>
      <button
        onClick={onProfilePress}
        style={{
          width:          30,
          height:         30,
          borderRadius:   '50%',
          border:         `1.5px solid ${colors.border}`,
          background:     colors.card,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          cursor:         'pointer',
          padding:        0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke={colors.textSecondary} strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
        </svg>
      </button>
    </div>
  )
}

// ─────────────────────────────────────────────
// PhrenesSplash — onboarding / launch screen
// ─────────────────────────────────────────────
interface SplashProps {
  onGetStarted?: () => void
  onSignIn?:     () => void
}

export function PhrenesSplash({ onGetStarted, onSignIn }: SplashProps) {
  return (
    <div style={{
      width:          '100%',
      minHeight:      '100vh',
      background:     colors.primary,
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        '40px 32px',
      position:       'relative',
      overflow:       'hidden',
    }}>
      {/* Decorative blobs */}
      <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: '#2a6356', opacity: 0.35, top: -160, left: -80, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', background: '#2a6356', opacity: 0.2,  bottom: -80, right: -50, pointerEvents: 'none' }} />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, zIndex: 1 }}>

        <PhrenosIcon size={80} variant="reversed" />

        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 44, fontWeight: 800, color: '#FFFFFF',
            letterSpacing: '-1.5px', lineHeight: 1,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}>
            phrenos
          </div>
          <div style={{ fontSize: 15, color: colors.mintText, marginTop: 10, lineHeight: 1.5 }}>
            Study sharper.
          </div>
        </div>

        <button onClick={onGetStarted} style={{
          background: '#FFFFFF', color: colors.primary, borderRadius: 999,
          padding: '16px 48px', fontSize: 16, fontWeight: 700,
          border: 'none', cursor: 'pointer', marginTop: 16, fontFamily: 'inherit',
        }}>
          Get started
        </button>

        <button onClick={onSignIn} style={{
          background: 'transparent', color: colors.mintText,
          border: 'none', fontSize: 13, cursor: 'pointer',
          padding: '4px 0', fontFamily: 'inherit',
        }}>
          Already have an account? Sign in
        </button>

        {/* Required ETS disclaimer */}
        <p style={{
          fontSize: 10, color: '#478475', textAlign: 'center',
          lineHeight: 1.6, maxWidth: 260, marginTop: 24,
        }}>
          GRE® is a registered trademark of ETS.{'\n'}
          Phrenos is not affiliated with or endorsed by ETS.
        </p>

      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Raw SVG strings — paste into public/ folder
// ─────────────────────────────────────────────

/** public/logo.svg  —  use as favicon source or OG image base */
export const logoSVGString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="120" height="120">
  <rect width="120" height="120" rx="26" fill="#1F4C40"/>
  <circle cx="60" cy="60" r="27" fill="none" stroke="#478475" stroke-width="4.5"/>
  <line x1="60" y1="16" x2="60" y2="104" stroke="white" stroke-width="4.5" stroke-linecap="round"/>
  <circle cx="60" cy="60" r="5" fill="white"/>
</svg>`

/** Reversed variant — for dark bg OG images, email headers, etc. */
export const logoReversedSVGString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="120" height="120">
  <rect width="120" height="120" rx="26" fill="#2d6454"/>
  <circle cx="60" cy="60" r="27" fill="none" stroke="#9FE1CB" stroke-width="4.5"/>
  <line x1="60" y1="16" x2="60" y2="104" stroke="white" stroke-width="4.5" stroke-linecap="round"/>
  <circle cx="60" cy="60" r="5" fill="white"/>
</svg>`

// ─────────────────────────────────────────────
// BrandPreview — visual QA page, remove in prod
// ─────────────────────────────────────────────
export default function BrandPreview() {
  const sectionLabel: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, letterSpacing: 1,
    textTransform: 'uppercase', color: colors.textSecondary,
    marginBottom: 14,
  }

  return (
    <div style={{ padding: 32, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: colors.background, minHeight: '100vh' }}>

      <div style={{ marginBottom: 36 }}>
        <PhrenosWordmark iconSize={40} showTagline />
      </div>

      <p style={sectionLabel}>Icon — all sizes</p>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 36 }}>
        {[120, 80, 48, 32, 20].map(s => (
          <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <PhrenosIcon size={s} />
            <span style={{ fontSize: 10, color: colors.textSecondary }}>{s}px</span>
          </div>
        ))}
      </div>

      <p style={sectionLabel}>Wordmark variants</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 36, alignItems: 'flex-start' }}>
        <div style={{ background: colors.card, padding: 20, borderRadius: 14, border: `1px solid ${colors.border}` }}>
          <PhrenosWordmark iconSize={36} variant="default" showTagline />
        </div>
        <div style={{ background: colors.primary, padding: 20, borderRadius: 14 }}>
          <PhrenosWordmark iconSize={36} variant="reversed" showTagline />
        </div>
        <div style={{ background: colors.background, padding: 20, borderRadius: 14, border: `1px solid ${colors.border}` }}>
          <PhrenosWordmark iconSize={36} variant="default" showTagline />
        </div>
      </div>

      <p style={sectionLabel}>App header</p>
      <div style={{ maxWidth: 360, border: `1px solid ${colors.border}`, borderRadius: 14, overflow: 'hidden', marginBottom: 36 }}>
        <AppHeader />
      </div>

      <p style={sectionLabel}>Color tokens</p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {Object.entries(colors).map(([name, hex]) => (
          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '8px 12px' }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: hex, border: `1px solid ${colors.border}`, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary }}>{name}</div>
              <div style={{ fontSize: 10, color: colors.textSecondary }}>{hex}</div>
            </div>
          </div>
        ))}
      </div>

    </div>
  )
}
